import {
  CustomFieldDetail,
  EndOrderDetails,
  CartItem,
  PaymentMethod,
  firestoreDateParser,
  OrderItemLineItem,
  AliquotAmount,
  isPaymentMethodWithLink,
  translate,
  formatTableLabel,
} from '@pedix-workspace/utils';
import {
  OrderHeadingParams,
  OrderItemsByCategories,
  OrderTotalParams,
  OrderItem,
} from '../order-message.types';
import { getOrderAddress, getOrderAddressHints } from '@pedix-workspace/shared-models';

import { format } from 'date-fns';
import { parseCustomFieldValue } from '../parse-custom-field-values';
import {
  endOrderCartItemToOrderItem,
  getCartItemsByCategory,
  getOrderItemRows,
  sortAsscending,
} from '../cart-item/cart-item-grouping';
import { cartItemsToCartView } from '../cart-view/cart-view';
import { getOrderPaymentMethodLabel } from '../order-payment-method-label';
import { getOrderPaymentDisplayStatus, isCollectedByMarketplace } from '../order-item.utils';
import {
  getOrderAmountDue,
  getOrderCollectedAmount,
  getOrderOutstandingAmount,
  getOrderPlatformDiscount,
} from '../order-payment-totals';

const getOrderCollectedStatus = (
  endOrder: EndOrderDetails,
): OrderHeadingParams['paymentStatus'] => {
  const paymentStatus = getOrderPaymentDisplayStatus(endOrder);
  return paymentStatus === 'PAID' || paymentStatus === 'PARTIAL' ? paymentStatus : undefined;
};
import { resolveTransferSource } from '../transfer-source';

export function endOrderToOrderHeadingParams(
  endOrder: EndOrderDetails,
  siteUrl: string,
  slug: string,
  translations: Record<string, string>,
): OrderHeadingParams {
  const displayMapUrl = endOrder.personalInformation.address?.addressDetails?.url;
  const paymentStatus = getOrderCollectedStatus(endOrder);
  const isPaid = paymentStatus === 'PAID';
  const transferSource = resolveTransferSource(endOrder);

  return {
    id: endOrder.id,
    sequenceId: endOrder.sequenceId,
    slug,
    date: firestoreDateParser(endOrder.created),
    name: endOrder.personalInformation.name,
    phone: endOrder.personalInformation.phone,
    paymentMethod:
      getOrderPaymentMethodLabel(endOrder, key => translate(translations, key)) || undefined,
    paymentMethodType: endOrder.paymentMethod?.type,
    paymentStatus,
    pendingBalance:
      paymentStatus === 'PARTIAL'
        ? getOrderOutstandingAmount({
            finalAmount: endOrder.finalAmount,
            paidAmount: getOrderCollectedAmount(endOrder),
            marketplaceOrder: endOrder.marketplaceOrder,
          })
        : undefined,
    couponCode: endOrder.couponConfigurationSnapshot?.code,
    marketplaceCode: endOrder.marketplaceOrder?.shortCode,
    finalAmount: endOrder.finalAmount,
    paymentMethodHowMuch: paymentStatus ? undefined : endOrder.howMuch,
    paymentLink:
      !paymentStatus && hasPaymentLink(endOrder.paymentMethod)
        ? getPaymentUrl(siteUrl, endOrder.id)
        : undefined,
    paymentLegend: isPaid ? undefined : endOrder.paymentMethod?.legend?.trim(),
    digitalPaymentCvu: transferSource?.cvu ?? undefined,
    digitalPaymentAccountHolder: transferSource?.accountHolder ?? undefined,
    digitalPaymentAlias: transferSource?.alias ?? undefined,
    digitalPaymentProvider: endOrder.digitalPayment?.provider,
    digitalPaymentStatusLink: endOrder.digitalPayment?.provider
      ? getReceiptUrl(siteUrl, slug, endOrder.id)
      : undefined,
    shippingOptionType: endOrder.shippingOption?.type,
    address: getOrderAddress(endOrder.personalInformation.address, endOrder.shippingOption?.type, {
      includeHints: false,
    }),
    addressHints: getOrderAddressHints(
      endOrder.personalInformation.address,
      endOrder.shippingOption?.type,
    ),
    mapUrl: displayMapUrl ? getMapUrl(siteUrl, endOrder.id) : undefined,
    tableNumber: endOrder.tableNumber,
    tableLabel: endOrder.table
      ? formatTableLabel(endOrder.table.name, endOrder.table.sectorName)
      : undefined,
    customFieldDetails: getSanitizedCustomFieldDetails(endOrder.customFieldDetails),
    trackingUrl: endOrder.shippingEstimate ? getTrackingUrl(siteUrl, endOrder.id) : undefined,
    shippingProviderName: endOrder.shippingEstimate?.providerName,
    parcelTrackingEmail: endOrder.personalInformation.email,
    shippingServiceType:
      endOrder.shippingEstimate?.serviceType || endOrder.shippingOrder?.serviceType,
  };
}

export function endOrderToOrderItemsByCategories(cartItems: CartItem[]): OrderItemsByCategories {
  const cartView = cartItemsToCartView(cartItems);

  return {
    categories: cartView.categories.map(category => ({
      categoryName: category.categoryName,
      hasCustomGroups: category.hasCustomGroups,
      groups: category.groups.map(group => ({
        name: group.name,
        isCustomGroup: group.isCustomGroup,
        items: group.productGroups.flatMap(productGroup => productGroup.orderItems),
      })),
    })),
  };
}

export function sortedCartItemsByCategoryAndGroup(cartItems: CartItem[]): CartItem[] {
  const cartItemsByCategory = getCartItemsByCategory(cartItems);
  const categoryNames = Object.keys(cartItemsByCategory);
  const sortedCartItems: CartItem[] = [];

  categoryNames.forEach(categoryName => {
    const itemsByGroup = cartItemsByCategory[categoryName];
    const groupNames = Object.keys(itemsByGroup);

    groupNames.sort(sortAsscending);

    groupNames.forEach(groupName => {
      itemsByGroup[groupName].forEach(cartItem => {
        sortedCartItems.push(cartItem);
      });
    });
  });

  return sortedCartItems;
}

export function endOrderLineItems(
  order: Pick<
    EndOrderDetails,
    | 'cartItems'
    | 'deliveryCost'
    | 'couponDiscount'
    | 'paymentMethodExtraCharge'
    | 'paymentMethodDiscount'
  >,
  params: { defaultAliquot?: AliquotAmount },
) {
  return endOrderCartItemsToOrderLineItems(order.cartItems, {
    defaultAliquot: params.defaultAliquot,
  }).concat(
    endOrderPriceModifiersToOrderLineItems(order, {
      defaultAliquot: params.defaultAliquot,
    }),
  );
}

export function endOrderCartItemsToOrderLineItems(
  cartItems: CartItem[],
  params: { defaultAliquot?: AliquotAmount },
): OrderItemLineItem[] {
  const sortedCartItems = sortedCartItemsByCategoryAndGroup(cartItems);

  return sortedCartItems
    .map(endOrderCartItemToOrderItem)
    .map(orderItem => orderItemToOrderItemLineItems(orderItem, params))
    .flat();
}

export function endOrderPriceModifiersToOrderLineItems(
  priceModifiers: Pick<
    EndOrderDetails,
    | 'deliveryCost'
    | 'couponDiscount'
    | 'paymentMethodExtraCharge'
    | 'paymentMethodDiscount'
    | 'promotionDiscount'
  >,
  params: { defaultAliquot?: AliquotAmount },
): OrderItemLineItem[] {
  const lineItems: OrderItemLineItem[] = [];

  if (priceModifiers.deliveryCost > 0) {
    lineItems.push({
      categoryName: undefined,
      groupName: undefined,
      name: 'Costo de envío',
      quantity: 1,
      unitPrice: priceModifiers.deliveryCost,
      total: priceModifiers.deliveryCost,
      aliquot: params.defaultAliquot,
    });
  }
  if (priceModifiers.paymentMethodExtraCharge > 0) {
    lineItems.push({
      categoryName: undefined,
      groupName: undefined,
      name: 'Cargos extra',
      quantity: 1,
      unitPrice: priceModifiers.paymentMethodExtraCharge,
      total: priceModifiers.paymentMethodExtraCharge,
      aliquot: params.defaultAliquot,
    });
  }
  if (priceModifiers.promotionDiscount > 0) {
    lineItems.push({
      categoryName: undefined,
      groupName: undefined,
      name: 'Descuento promociones',
      quantity: 1,
      unitPrice: priceModifiers.promotionDiscount * -1,
      total: priceModifiers.promotionDiscount * -1,
      aliquot: params.defaultAliquot,
    });
  }
  if (priceModifiers.couponDiscount > 0 || priceModifiers.paymentMethodDiscount > 0) {
    const totalDiscount =
      ((priceModifiers.couponDiscount || 0) + (priceModifiers.paymentMethodDiscount || 0)) * -1;

    lineItems.push({
      categoryName: undefined,
      groupName: undefined,
      name: 'Descuentos',
      quantity: 1,
      unitPrice: totalDiscount,
      total: totalDiscount,
      aliquot: params.defaultAliquot,
    });
  }
  return lineItems;
}

export function orderItemToOrderItemLineItems(
  orderItem: OrderItem,
  params: { defaultAliquot?: AliquotAmount },
): OrderItemLineItem[] {
  const lineItems: OrderItemLineItem[] = [];

  getOrderItemRows(orderItem).forEach(row => {
    const presentationName = row.presentationSource?.name;
    lineItems.push({
      categoryName: orderItem.cartItem.category.name,
      groupName: orderItem.cartItem.product.groupName,
      name: presentationName
        ? `${orderItem.productName} - ${presentationName}`
        : orderItem.productName,
      quantity: row.quantity,
      unitPrice: row.unitPrice,
      total: row.total,
      sku: row.sku,
      aliquot: orderItem.cartItem.product.aliquot ?? params.defaultAliquot,
    });
  });

  orderItem.options?.forEach(option => {
    option.items.forEach(item => {
      lineItems.push({
        categoryName: orderItem.cartItem.category.name,
        groupName: orderItem.cartItem.product.groupName,
        name: item.name,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        total: item.price,
        aliquot: item.aliquot ?? params.defaultAliquot,
      });
    });
  });

  return lineItems.filter(lineItem => lineItem.total > 0);
}

export function getEndOrderToOrderTotalParams(endOrder: EndOrderDetails): OrderTotalParams {
  const deliveryCost = endOrder.deliveryCost;
  const outOfDeliveryZone = endOrder.outOfDeliveryZone;

  return {
    totalAmount: endOrder.totalAmount,
    totalProductDiscount: endOrder.totalProductDiscount,
    promotionDiscount: endOrder.promotionDiscount,
    finalAmount: endOrder.finalAmount,
    platformDiscount: getOrderPlatformDiscount(endOrder) || undefined,
    amountDue:
      getOrderPlatformDiscount(endOrder) > 0 && !isCollectedByMarketplace(endOrder)
        ? getOrderAmountDue(endOrder)
        : undefined,
    couponDiscount: endOrder.couponDiscount,
    deliveryCost,
    outOfDeliveryZone,
    extraChargeAmount: endOrder.paymentMethodExtraCharge,
    discountAmount: endOrder.paymentMethodDiscount,
    installmentSurcharge: endOrder.installmentSurcharge,
    foodBankDonationAmount: endOrder.foodBankDonationAmount,
  };
}

export function getSanitizedCustomFieldDetails(customFieldDetails?: CustomFieldDetail[]) {
  if (!customFieldDetails || customFieldDetails?.length === 0) {
    return undefined;
  }
  const sanitizedCustomFields = customFieldDetails
    .map(customFieldDetail => ({
      name: customFieldDetail.field.name,
      value: getCustomFieldDetailsValue(customFieldDetail),
    }))
    .filter(customField => Boolean(customField.value));

  return sanitizedCustomFields.length > 0 ? sanitizedCustomFields : undefined;
}

export function getCustomFieldDetailsValue(customFieldDetail: CustomFieldDetail): string {
  const parsedValue = parseCustomFieldValue(
    customFieldDetail.value,
    customFieldDetail.field.fieldType,
  );

  if (!parsedValue) {
    return null;
  }

  if (Array.isArray(parsedValue)) {
    return parsedValue.join(', ');
  }

  if (parsedValue instanceof Date) {
    return format(parsedValue, 'dd/MM/yy');
  }

  return parsedValue.toString();
}

export function hasPaymentLink(paymentMethod: PaymentMethod | undefined | null) {
  return (
    paymentMethod !== null && paymentMethod !== undefined && isPaymentMethodWithLink(paymentMethod)
  );
}

export function getMapUrl(siteUrl: string, orderId: string) {
  return `${siteUrl}/pedidos/${orderId}/mapa`;
}

export function getPaymentUrl(siteUrl: string, orderId: string) {
  return `${siteUrl}/pedidos/${orderId}/pago`;
}

export function getReceiptUrl(siteUrl: string, slug: string, orderId: string) {
  return `${siteUrl}/${slug}/pagos/${orderId}`;
}

export function getTrackingUrl(siteUrl: string, orderId: string) {
  return `${siteUrl}/pedidos/${orderId}/envio`;
}

export const applyFormatToEveryLine = (
  message: string,
  formatLine: (line: string, index?: number) => string,
) => {
  const lines = message.split(/\n|\r/);

  return lines.map((line, index) => (line ? formatLine(line, index) : line)).join('\n');
};

export const hasAnyPriceModifier = (
  priceModifiers: Pick<
    OrderTotalParams,
    | 'totalProductDiscount'
    | 'promotionDiscount'
    | 'deliveryCost'
    | 'couponDiscount'
    | 'extraChargeAmount'
    | 'discountAmount'
    | 'installmentSurcharge'
    | 'foodBankDonationAmount'
  >,
): boolean => {
  return [
    priceModifiers.totalProductDiscount,
    priceModifiers.promotionDiscount,
    priceModifiers.deliveryCost,
    priceModifiers.couponDiscount,
    priceModifiers.discountAmount,
    priceModifiers.extraChargeAmount,
    priceModifiers.installmentSurcharge,
    priceModifiers.foodBankDonationAmount,
  ].some(priceModifier => priceModifier > 0);
};
